import { type Level, InitErreurDefault, type ErreurState } from '@/features/domaine/moteur-regles/index.js'
import type { ModelState } from '@/features/domaine/types/techniques'
import type { DirtyState, DirtifyErreurState } from './index.js'
import { mergeModelState } from '@/features/domaine/model-state-tools'

export function dirtifyErreur<TModel extends object, TLevel extends Level = Level>(
  erreur: ModelState<TModel, ErreurState<TLevel>>,
  dirtyState: ModelState<NoInfer<TModel>, DirtyState>,
) {
  return mergeModelState(erreur, dirtyState as ModelState<TModel, DirtyState & DirtifyErreurState>, {
    defaultA: InitErreurDefault<TModel, TLevel>(),
    defaultB: {
      $estDirty: false,
      $estErreurDirty: false,
      $messageErreurDirty: null,
    },
    compute: (state) => {
      if (state.$estDirty === false || state.$estErreur === false) return state
      return { ...state, $estErreurDirty: true, $messageErreurDirty: state.$messageErreur }
    },
  })
}

// Todo : ancienne version à supprimer
/*
export function dirtifyErreurOld<TModel extends object, TLevel extends Level>(
  erreur: Erreur<TModel, TLevel>,
  dirtyState: Dirty<TModel>,
): ErreurWithDirty<TModel, TLevel> {
  const erreurModel = erreur as ErreurWithDirty<TModel, TLevel>
  const dirtyModel = dirtyState as ErreurWithDirty<TModel, TLevel>
  return fusionneErreurWithDirty<TModel, TLevel>(erreurModel, dirtyModel)
}

function creerErreurWithDirty<TModel, TLevel extends number>(
  options?: Partial<ErreurWithDirtyState<TLevel>>,
): ErreurWithDirty<TModel, TLevel> {
  const dirtyDefault = {
    $estDirty: false,
    $estErreurDirty: false,
    $messageErreurDirty: null,
    ...InitErreurDefault<TModel, TLevel>(),
  } as ErreurWithDirty<TModel, TLevel>

  return {
    ...dirtyDefault,
    ...(options ?? {}),
  } as ErreurWithDirty<TModel, TLevel>
}

function fusionneErreurWithDirty<TModel, TLevel extends Level>(
  ...args: Erreur<TModel, TLevel>[]
): ErreurWithDirty<TModel, TLevel> {
  let result = creerErreurWithDirty<TModel, TLevel>()
  for (const erreur of args) {
    result = fusionneErreurWithDirtyInt(result, erreur)
  }
  return result
}

function fusionneErreurWithDirtyInt<TModel extends object, TLevel extends Level>(
  destination: Erreur<TModel, TLevel>,
  patch: Erreur<TModel, TLevel>,
): ErreurWithDirty<TModel, TLevel> {
  if (Array.isArray(destination)) {
    destination = Object.assign([...destination], creerErreurWithDirty(destination))
  } else {
    destination = creerErreurWithDirty(destination ?? {})
  }

  if (patch?.$estErreur) {
    destination.$estErreur = true
    destination.$level = patch.$level
    destination.$messageErreur = patch.$messageErreur
    destination.$origine = patch.$origine
  }
  const destinationDirty: ModelStateObject<TModel, ErreurWithDirtyState<TLevel>> = destination
  if (patch && '$estDirty' in patch && patch.$estDirty === true) {
    destinationDirty.$estDirty = true
    destinationDirty.$estErreurDirty = destination.$estErreur
    destinationDirty.$messageErreurDirty = destination.$messageErreur
  }

  if (Array.isArray(patch)) {
    let destinationArray = destination as ModelStateArray<TModel, ErreurWithDirtyState<TLevel>>
    if (Array.isArray(destinationArray) === false) {
      destinationArray = Object.assign([], creerErreurWithDirty(destinationArray)) as any
    }
    for (let index = 0; index < patch.length; index++) {
      destinationArray[index] = fusionneErreurWithDirtyInt((destination as any)[index], patch[index])
    }
    return destinationArray as ErreurWithDirty<TModel, TLevel>
  }

  for (const key in patch) {
    if (key.startsWith('$')) continue
    ;(destination as any)[key] = fusionneErreurWithDirtyInt((destination as any)[key], (patch as any)[key])
  }
  return destination as ErreurWithDirty<TModel, TLevel>
}
*/

